import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  PropsWithChildren,
  useContext,
  useState,
  Dispatch,
  SetStateAction
} from 'react';
import { toast } from 'react-toastify';
import { Cart } from '../../models/Cart/Cart';
import Session from '../../utils/storage/ClientStorage';

interface CartContextProviderProps extends PropsWithChildren {
  storageKey?: string; // Optional prop to define the session storage key
}

export interface CartContextState {
  cart: Cart | null;
  isLoading: boolean;
  includeWashingFee: boolean;
  setIncludeWashingFee: Dispatch<SetStateAction<boolean>>;
  cartSetStartEndDates: (startDate: string, endDate: string) => Promise<void>;
  cartAddProduct: (productID: number, quantity: number) => Promise<void>;
  cartRemoveProduct: (productID: number) => Promise<void>;
  cartChangeProductQuantity: (
    productID: number,
    quantity: number
  ) => Promise<void>;
  cartClear: () => Promise<void>;
}

const initialState: CartContextState = {
  cart: null,
  isLoading: true,
  includeWashingFee: false,
  setIncludeWashingFee: () => {},
  cartSetStartEndDates: async () => {},
  cartAddProduct: async () => {},
  cartRemoveProduct: async () => {},
  cartChangeProductQuantity: async () => {},
  cartClear: async () => {}
};

export const CartContext = createContext(initialState);

export const useCartContext = () => useContext(CartContext);

// --- UPDATED COMPONENT SIGNATURE ---
function CartContextProvider({
  children,
  storageKey = 'cart_token' // Default to 'cart_token' for public site
}: CartContextProviderProps) {
  const [cart, setCart] = useState<Cart | null>(null);
  const [includeWashingFee, setIncludeWashingFee] = useState(false);
  const queryClient = useQueryClient();

  // The query key is now unique based on the storageKey, isolating the carts
  const cartQueryKey = ['cart', storageKey];

  const { isLoading } = useQuery(
    cartQueryKey,
    () => Cart.get(Session.get(storageKey) ?? null),
    {
      onSuccess(newCart) {
        Session.set(storageKey, newCart.token);
        setCart(newCart);
      },
      onError(error) {
        console.error(`Failed to fetch cart for key "${storageKey}":`, error);
      }
    }
  );

  const updateCartState = (updatedCart: Cart) => {
    setCart(updatedCart);
    queryClient.setQueryData(cartQueryKey, updatedCart);
  };

  const cartSetStartEndDates = async (startDate: string, endDate: string) => {
    if (!cart) return;

    // Only update if both dates exist
    if (!startDate || !endDate) return;

    try {
      const updatedCart = await cart.setStartEndDates(startDate, endDate);
      updateCartState(updatedCart);
    } catch (error) {
      console.error('Error setting cart dates:', error);
      toast.error('Greška pri postavljanju datuma rezervacije.');
      // Don't throw here; just handle gracefully
    }
  };

  const cartAddProduct = async (productID: number, quantity: number) => {
    if (!cart) return;
    try {
      const updatedCart = await cart.addProduct(productID, quantity);
      updateCartState(updatedCart);
    } catch (error) {
      console.error('Error adding product to cart:', error);
      toast.error('Greška pri dodavanju proizvoda u korpu.');
    }
  };

  const cartRemoveProduct = async (productID: number) => {
    if (!cart) return;
    try {
      const updatedCart = await cart.removeProduct(productID);
      updateCartState(updatedCart);
    } catch (error) {
      console.error('Error removing product from cart:', error);
      toast.error('Greška pri uklanjanju proizvoda iz korpe.');
    }
  };

  const cartChangeProductQuantity = async (
    productID: number,
    quantity: number
  ) => {
    if (!cart) return;
    try {
      const updatedCart = await cart.changeProductQuantity(productID, quantity);
      updateCartState(updatedCart);
    } catch (error) {
      console.error('Error changing product quantity:', error);
      toast.error('Greška pri promeni količine proizvoda.');
    }
  };

  const cartClear = async () => {
    if (!cart) return;
    try {
      // 1) clear the server‐side contents
      await cart.emptyCart();

      // 2) drop the token so our next useQuery() will spin up a fresh cart
      Session.delete(storageKey);

      // 3) purge React Query’s cache for this cart key
      queryClient.removeQueries(cartQueryKey);

      // 4) reset local state immediately if you like
      setCart(null);
    } catch (error) {
      console.error('Error clearing cart:', error);
      toast.error('Greška pri brisanju korpe.');
    }
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        isLoading,
        includeWashingFee,
        setIncludeWashingFee,
        cartSetStartEndDates,
        cartAddProduct,
        cartRemoveProduct,
        cartChangeProductQuantity,
        cartClear
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export default CartContextProvider;
